/**
 * <kk-product-media> — behaviour of blocks/kk-product-media.liquid.
 *
 * - Carousel = a scroll-snap track (works by swiping / scrolling without JS). Thumbnails, dots and arrows
 *   scroll it; the active thumbnail / dot follows the scroll position.
 * - Zoom opens a lightbox <dialog> with the large image, prev / next and Esc / backdrop to close.
 * - Listens for the document event kk:variant:change { mediaId } and jumps to that media.
 * - Videos are paused when you move to another slide.
 */
class KKProductMedia extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.track = this.querySelector('[data-kk-pm-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.thumbs = [...this.querySelectorAll('[data-kk-pm-thumb]')];
    this.dots = [...this.querySelectorAll('[data-kk-pm-dot]')];
    this.thumbList = this.querySelector('[data-kk-pm-thumb-list]');
    this.prev = this.querySelector('[data-kk-pm-prev]');
    this.next = this.querySelector('[data-kk-pm-next]');
    this.lightbox = this.querySelector('[data-kk-pm-lightbox]');
    this.lightboxImage = this.querySelector('[data-kk-pm-lightbox-image]');
    this.index = 0;
    this.lightboxIndex = 0;

    this.track.addEventListener('scroll', () => this.#queueUpdate(), { passive: true, signal });
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i), { signal }));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i), { signal }));
    this.prev?.addEventListener('click', () => this.goTo(this.index - 1), { signal });
    this.next?.addEventListener('click', () => this.goTo(this.index + 1), { signal });

    this.querySelector('[data-kk-pm-viewer]')?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
        else if (event.key === 'ArrowRight') this.goTo(this.index + 1);
      },
      { signal }
    );

    this.querySelectorAll('[data-kk-pm-zoom]').forEach((button) => {
      button.addEventListener('click', () => this.#openLightbox(Number(button.dataset.index) || 0), { signal });
    });
    this.querySelector('[data-kk-pm-lightbox-close]')?.addEventListener('click', () => this.lightbox?.close(), { signal });
    this.querySelector('[data-kk-pm-lightbox-prev]')?.addEventListener('click', () => this.#stepLightbox(-1), { signal });
    this.querySelector('[data-kk-pm-lightbox-next]')?.addEventListener('click', () => this.#stepLightbox(1), { signal });
    this.lightbox?.addEventListener('click', (event) => event.target === this.lightbox && this.lightbox.close(), { signal });
    this.lightbox?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') this.#stepLightbox(-1);
        else if (event.key === 'ArrowRight') this.#stepLightbox(1);
      },
      { signal }
    );

    document.addEventListener('kk:variant:change', (event) => this.#onVariant(event), { signal });
    this.#update();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    cancelAnimationFrame(this.frame);
  }

  /** @param {number} index */
  goTo(index) {
    const target = Math.max(0, Math.min(this.slides.length - 1, index));
    const slide = this.slides[target];
    if (!slide) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const left = slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
    this.track.scrollTo({ left, behavior: reduced ? 'auto' : 'smooth' });
  }

  #onVariant(event) {
    const mediaId = String(event.detail?.mediaId ?? '');
    if (!mediaId) return;
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
    if (index >= 0) this.goTo(index);
  }

  #queueUpdate() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const width = this.track.clientWidth || 1;
    const grid = this.dataset.presentation === 'grid';
    const index = grid ? 0 : Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / width)));
    const changed = index !== this.index;
    this.index = index;

    this.thumbs.forEach((thumb, i) => (i === index ? thumb.setAttribute('aria-current', 'true') : thumb.removeAttribute('aria-current')));
    this.dots.forEach((dot, i) => (i === index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current')));
    if (this.prev) this.prev.disabled = index <= 0;
    if (this.next) this.next.disabled = index >= this.slides.length - 1;

    const activeThumb = this.thumbs[index];
    if (activeThumb && this.thumbList) {
      const vertical = this.thumbList.scrollHeight > this.thumbList.clientHeight + 1 && getComputedStyle(this.thumbList).flexDirection === 'column';
      if (vertical) this.thumbList.scrollTop = activeThumb.offsetTop - (this.thumbList.clientHeight - activeThumb.offsetHeight) / 2;
      else this.thumbList.scrollLeft = activeThumb.offsetLeft - (this.thumbList.clientWidth - activeThumb.offsetWidth) / 2;
    }

    if (changed) this.slides.forEach((slide, i) => i !== index && slide.querySelectorAll('video').forEach((video) => video.pause()));
  }

  /** @param {number} index */
  #openLightbox(index) {
    if (!this.lightbox || !this.lightboxImage) return;
    this.lightboxIndex = index;
    this.#showLightboxImage();
    this.lightbox.showModal();
  }

  /** @param {number} direction */
  #stepLightbox(direction) {
    const zoomable = this.slides.map((slide, i) => (slide.querySelector('[data-kk-pm-zoom]') ? i : -1)).filter((i) => i >= 0);
    if (zoomable.length < 2) return;
    const position = zoomable.indexOf(this.lightboxIndex);
    this.lightboxIndex = zoomable[(position + direction + zoomable.length) % zoomable.length];
    this.#showLightboxImage();
  }

  #showLightboxImage() {
    const button = this.slides[this.lightboxIndex]?.querySelector('[data-kk-pm-zoom]');
    if (!button || !this.lightboxImage) return;
    this.lightboxImage.src = button.dataset.full || '';
    this.lightboxImage.alt = button.dataset.alt || '';
  }
}

if (!customElements.get('kk-product-media')) {
  customElements.define('kk-product-media', KKProductMedia);
}
